import type { Vetor3 } from '../geometria/vetor3'

/**
 * Avaliação da cena: o que a equipe reconhece ANTES de agir sobre o veículo.
 *
 * As oito categorias são exatamente as que o `ideia.md` §6 lista — nenhuma a
 * mais. O documento é a especificação, e acrescentar categorias por conta
 * própria seria inventar conteúdo, o que o §13 proíbe.
 *
 * O texto de cada categoria é OBSERVACIONAL: o que procurar e por que aquilo
 * muda a leitura da cena. Não há sequência operacional nem protocolo aqui —
 * isso é do instrutor, e o mesmo cuidado já vale para `procedimentosInternos`.
 */
export type TipoRisco =
  | 'ambiental'
  | 'transito'
  | 'vazamento'
  | 'energiaEletrica'
  | 'instabilidadeVeiculo'
  | 'obstaculo'
  | 'sistemasSeguranca'
  | 'posicaoVitimas'

export interface CategoriaRisco {
  tipo: TipoRisco
  nome: string
  /** o que procurar na cena */
  oQueObservar: string
  /** por que reconhecer isso muda a leitura */
  porQueImporta: string
  /**
   * Se costuma ser reconhecido OLHANDO ou RACIOCINANDO.
   *
   * A distinção é o coração do exercício: uma poça no asfalto se vê; a
   * instabilidade de um veículo apoiado não se vê, se deduz da posição em que
   * ele parou. O cenário pode contrariar o padrão — é o campo `visivel` de cada
   * risco que decide, não este.
   */
  costumaSerVisivel: boolean
}

export const CATEGORIAS_RISCO: Readonly<Record<TipoRisco, CategoriaRisco>> = {
  ambiental: {
    tipo: 'ambiental',
    nome: 'Riscos ambientais',
    oQueObservar: 'Terreno, inclinação, piso escorregadio, vegetação, iluminação e condições do tempo.',
    porQueImporta: 'Determinam onde a equipe pode se posicionar com segurança e como os equipamentos se apoiam.',
    costumaSerVisivel: true,
  },
  transito: {
    tipo: 'transito',
    nome: 'Trânsito',
    oQueObservar: 'Sentido e volume do fluxo, visibilidade dos condutores que se aproximam e o espaço de bloqueio.',
    porQueImporta: 'É risco para a equipe de costas para a via, e não para a vítima. Define o lado de trabalho.',
    costumaSerVisivel: true,
  },
  vazamento: {
    tipo: 'vazamento',
    nome: 'Vazamentos',
    oQueObservar: 'Poças e escorrimento sob o veículo, cheiro de combustível, marcas no asfalto.',
    porQueImporta: 'Muda o que pode ser feito com ferramenta que gera faísca ou calor, e para onde o produto escorre.',
    costumaSerVisivel: true,
  },
  energiaEletrica: {
    tipo: 'energiaEletrica',
    nome: 'Energia elétrica',
    oQueObservar: 'Rede externa atingida, poste abalroado e, no veículo, a bateria e o sistema de alta tensão dos híbridos e elétricos.',
    porQueImporta: 'Energia presente muda a ordem das ações e o que pode ser cortado.',
    costumaSerVisivel: false,
  },
  instabilidadeVeiculo: {
    tipo: 'instabilidadeVeiculo',
    nome: 'Estabilidade',
    oQueObservar: 'Posição em que o veículo parou, apoio sobre rodas ou lataria, folga sob os pontos de contato e vazios.',
    porQueImporta: 'Um veículo que ainda pode se mover é risco para a equipe e para a vítima durante todo o atendimento.',
    costumaSerVisivel: false,
  },
  obstaculo: {
    tipo: 'obstaculo',
    nome: 'Obstáculos',
    oQueObservar: 'O que limita aproximação, abertura de portas, entrada de equipamento e a própria rota de retirada.',
    porQueImporta: 'Obstáculo reconhecido cedo muda o plano; reconhecido tarde, custa a manobra já iniciada.',
    costumaSerVisivel: true,
  },
  sistemasSeguranca: {
    tipo: 'sistemasSeguranca',
    nome: 'Sistemas de segurança veicular',
    oQueObservar: 'Quais dispositivos o veículo declara e quais NÃO dispararam no evento.',
    porQueImporta: 'Um módulo íntegro ainda pode disparar durante o corte e a retirada.',
    costumaSerVisivel: false,
  },
  posicaoVitimas: {
    tipo: 'posicaoVitimas',
    nome: 'Posição de veículos e vítimas',
    oQueObservar: 'Quantos veículos, quantas vítimas, onde estão e quem está fora do veículo.',
    porQueImporta: 'É o que dimensiona o atendimento antes de qualquer ação sobre a lataria.',
    costumaSerVisivel: true,
  },
}

export const TIPOS_RISCO = Object.keys(CATEGORIAS_RISCO) as TipoRisco[]

export function categoriaRisco(tipo: TipoRisco): CategoriaRisco {
  return CATEGORIAS_RISCO[tipo]
}

/** Um risco concreto de um cenário, como o JSON já os descreve. */
export interface RiscoCena {
  tipo: TipoRisco
  /** onde ele se manifesta, em coordenadas de mundo */
  posicao: Vetor3
  /** dá para reconhecer olhando a cena, ou só raciocinando sobre ela */
  visivel: boolean
}

export type EstadoMarcacao = 'confirmado' | 'semSustentacao'

export interface LeituraRisco {
  tipo: TipoRisco
  estado: EstadoMarcacao
  /** o risco do cenário, quando a marcação se confirma */
  risco: RiscoCena | null
}

export interface AvaliacaoCena {
  leituras: readonly LeituraRisco[]
  /** riscos do cenário ainda não reconhecidos */
  pendentes: readonly RiscoCena[]
  /** dos pendentes, os que não se resolvem olhando */
  pendentesOcultos: readonly RiscoCena[]
  /** categorias marcadas sem nada na cena que as sustente */
  semSustentacao: readonly TipoRisco[]
  progresso: number
  completa: boolean
  mensagem: string
}

/**
 * Avalia o que o aluno reconheceu na cena.
 *
 * Marcar uma categoria que o cenário não tem não é "errado" no sentido de
 * pontuação — é uma leitura sem sustentação, e o painel diz isso. O exercício é
 * reconhecer o que está lá, não adivinhar uma lista.
 *
 * O que separa um bom reconhecimento de um passeio de câmera é `visivel`: os
 * riscos que não se veem só entram na conta se o aluno os levantar a partir da
 * situação. É por isso que eles são contados à parte.
 */
export function avaliarCena(
  riscos: readonly RiscoCena[],
  marcados: readonly TipoRisco[],
): AvaliacaoCena {
  const unicos = [...new Set(marcados)]
  const leituras: LeituraRisco[] = unicos.map((tipo) => {
    const risco = riscos.find((r) => r.tipo === tipo) ?? null
    return { tipo, estado: risco ? 'confirmado' : 'semSustentacao', risco }
  })

  const confirmados = new Set(
    leituras.filter((l) => l.estado === 'confirmado').map((l) => l.tipo),
  )
  const pendentes = riscos.filter((r) => !confirmados.has(r.tipo))
  const pendentesOcultos = pendentes.filter((r) => !r.visivel)
  const semSustentacao = leituras.filter((l) => l.estado === 'semSustentacao').map((l) => l.tipo)

  const total = new Set(riscos.map((r) => r.tipo)).size
  const progresso = total === 0 ? 1 : confirmados.size / total
  const completa = pendentes.length === 0

  return {
    leituras,
    pendentes,
    pendentesOcultos,
    semSustentacao,
    progresso,
    completa,
    mensagem: completa
      ? 'Todos os riscos deste cenário foram reconhecidos. A avaliação continua durante o atendimento.'
      : pendentesOcultos.length === pendentes.length
        ? 'O que se via já foi reconhecido. Falta o que não se vê: olhe a situação, não a cena.'
        : 'Percorra a cena e reconheça o que ela mostra.',
  }
}
